import {
  InitialTargetShareOption,
  TrancheData,
} from 'further-types/allocations';
import { round } from 'lodash';

export interface TargetValues {
  targetShares: number;
  targetInvestment: number;
  targetInvestmentIncTransactionFee: number;
  targetAllocationPercent: number;
  targetAllocationPercentUnrounded: number;
  flexSharePrice?: number;
}

export interface CalculationParams {
  trancheData: TrancheData;
  initialTargetShareSelected: InitialTargetShareOption;
  dealSharePrice: number;
}

/**
 * Calculates target values based on the initial target share option selected
 */
const calculateTargetValues = ({
  trancheData,
  initialTargetShareSelected,
  dealSharePrice,
}: CalculationParams): TargetValues => {
  const {
    totalTrancheInvestmentCapital,
    remainingTrancheInvestmentCapital,
    defaultTransactionFeePercent,
    defaultTransactionFeeVatPercent = 0,
    initialTargetShares: initialTargetValue,
    fixedTargetShares,
  } = trancheData;

  const transactionFeeMultiplier =
    1 +
    (defaultTransactionFeePercent +
      defaultTransactionFeePercent * (defaultTransactionFeeVatPercent / 100)) /
      100;

  let targetShares: number;

  switch (initialTargetShareSelected) {
    case InitialTargetShareOption.InitialTargetShares: {
      const maxSharesForRemainingCapital = Math.floor(
        remainingTrancheInvestmentCapital /
          (dealSharePrice * transactionFeeMultiplier),
      );

      if (
        initialTargetValue * dealSharePrice * transactionFeeMultiplier >
        remainingTrancheInvestmentCapital
      ) {
        // Target shares = (Investment capital remaining in tranche / share price * (1+transaction fee %) - rounded down to the nearest whole number)
        targetShares = maxSharesForRemainingCapital;
      } else {
        // Target shares = Initial target shares
        targetShares = initialTargetValue;
      }
      break;
    }

    case InitialTargetShareOption.InitialTargetInvestment: {
      if (initialTargetValue > remainingTrancheInvestmentCapital) {
        // Target shares = (Investment capital remaining in tranche / share price * (1+transaction fee %) - rounded down to the nearest whole number)
        targetShares = Math.floor(
          remainingTrancheInvestmentCapital /
            (dealSharePrice * transactionFeeMultiplier),
        );
      } else {
        // Target shares = Initial target investment / share price * (1+transaction fee %), rounded down to the nearest whole number
        targetShares = Math.floor(
          initialTargetValue / (dealSharePrice * transactionFeeMultiplier),
        );
      }
      break;
    }

    case InitialTargetShareOption.InitialTargetAllocationPercent: {
      const maxInvestmentForAllocation = Math.min(
        (initialTargetValue / 100) * totalTrancheInvestmentCapital,
        remainingTrancheInvestmentCapital,
      );

      // Target shares = MIN(Initial target allocation % * Total tranche investment capital, Investment capital remaining in tranche) / share price * (1+transaction fee %), rounded down to the lowest whole number
      targetShares = Math.floor(
        maxInvestmentForAllocation /
          (dealSharePrice * transactionFeeMultiplier),
      );
      break;
    }

    case InitialTargetShareOption.InitialTargetInvestmentAndShares: {
      targetShares = trancheData.fixedTargetShares ?? 0;
      break;
    }

    default:
      throw new Error(
        `Unknown initial target share option: ${initialTargetShareSelected}`,
      );
  }

  // Target investment = target shares * share price rounded to the nearest penny
  const targetInvestment = fixedTargetShares
    ? initialTargetValue
    : round(targetShares * dealSharePrice, 2);

  // Target allocation % - the proportion of the Total tranche investment capital going into this deal (= target investment * (1+transaction fee %)/Total tranche investment capital), expressed as a % to 2 DP
  const targetAllocationPercent = round(
    ((targetInvestment * transactionFeeMultiplier) /
      totalTrancheInvestmentCapital) *
      100,
    8,
  );

  // Target investment (inc transaction fee) for each tranche is simply target investment * (1 + transaction fee %) rounded to the nearest penny
  const targetInvestmentIncTransactionFee = round(
    targetInvestment * transactionFeeMultiplier,
    2,
  );

  return {
    targetShares: targetShares ?? 0,
    targetInvestment,
    flexSharePrice: fixedTargetShares
      ? round(initialTargetValue / fixedTargetShares, 10)
      : undefined,
    targetInvestmentIncTransactionFee: targetInvestmentIncTransactionFee ?? 0,
    targetAllocationPercent,
    targetAllocationPercentUnrounded: targetAllocationPercent,
  };
};

export default calculateTargetValues;
